/**
 * The shared component set — LV §124, §125.
 *
 * These are the slots LV §124 names — FilterBar, DataView, DetailDrawer, Modal,
 * Validation, Permission Guards — so every domain page is composed the same way and a
 * user learns one layout rather than twelve.
 *
 * LV §125 lists what must *not* come across from the prototype: `contenteditable`,
 * `prompt()`, `confirm()`, inline `onclick`. Nothing here uses any of them. `Confirm`
 * is a real dialog with a real cancel button, and every form has explicit save/cancel
 * with dirty-state detection.
 */

import {
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Info } from "lucide-react";
import { z } from "zod";

import { hasPermission } from "@/auth/session";
import { useSession } from "@/auth/useSession";
import { ApiError } from "@/services/http";
import { LV_SECTION_TITLES } from "@/lib/lv-sections";
import { formatValue } from "@/lib/values";
import { pflichttext, useSchemaForm } from "@/validation";

// ==================================================================== page (LV §124)
export function Page({
  title,
  subtitle,
  actions,
  children,
}: {
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  useEffect(() => {
    document.title = `${title} · DAH Maintenance Hub`;
  }, [title]);

  return (
    <div className="page">
      <header className="page__header">
        <div className="page__title">
          <h1>{title}</h1>
          {subtitle ? <div className="page__subtitle">{subtitle}</div> : null}
        </div>
        {actions ? <div className="page__actions">{actions}</div> : null}
      </header>
      {children}
    </div>
  );
}

export function Card({
  title,
  actions,
  footer,
  children,
  padded = true,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  padded?: boolean;
}) {
  return (
    <section className="card">
      {title || actions ? (
        <div className="card__header">
          <div className="card__title">{title}</div>
          {actions ? <div className="row">{actions}</div> : null}
        </div>
      ) : null}
      <div className={padded ? "card__body" : undefined}>{children}</div>
      {footer ? <div className="card__footer">{footer}</div> : null}
    </section>
  );
}

// ==================================================================== FilterBar
export function FilterBar({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`filter-bar${className ? ` ${className}` : ""}`}>{children}</div>
  );
}

export function FilterField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="filter-bar__field">
      <span className="filter-bar__label">{label}</span>
      {children}
    </label>
  );
}

// ==================================================================== buttons
type ButtonVariant = "default" | "primary" | "danger" | "ghost";

export function Button({
  variant = "default",
  size,
  type = "button",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: "sm";
}) {
  const classes = [
    "btn",
    variant !== "default" ? `btn--${variant}` : "",
    size ? `btn--${size}` : "",
    props.className ?? "",
  ]
    .filter(Boolean)
    .join(" ");
  return <button {...props} type={type} className={classes} />;
}

// ==================================================================== badge
export type BadgeTone = "neutral" | "ok" | "warning" | "danger" | "info" | "accent";

export function Badge({
  tone = "neutral",
  children,
  title,
}: {
  tone?: BadgeTone | "a" | "b" | "c";
  children: ReactNode;
  title?: string;
}) {
  return (
    <span
      className={`badge${tone !== "neutral" ? ` badge--${tone}` : ""}`}
      title={title}
    >
      {children}
    </span>
  );
}

// ==================================================================== LV references
/** Every section a citation names: "§90, §187" is two, "§58-61" is four. */
function citedSections(sections: string): number[] {
  const numbers: number[] = [];
  for (const match of sections.matchAll(/§\s*(\d+)(?:\s*[-–]\s*§?\s*(\d+))?/g)) {
    const first = Number(match[1]);
    const last = match[2] ? Number(match[2]) : first;
    for (let section = first; section <= last; section += 1) numbers.push(section);
  }
  return numbers;
}

/**
 * Below the ⓘ, or above it where the viewport ends; never past either side. Coordinates
 * are the viewport's, which is what a top-layer element is positioned against.
 */
function placePanel(panel: HTMLElement, anchor: HTMLElement): void {
  const gap = 6;
  const margin = 8;
  const at = anchor.getBoundingClientRect();
  const size = panel.getBoundingClientRect();
  const fitsBelow = at.bottom + gap + size.height <= window.innerHeight - margin;
  const top = fitsBelow
    ? at.bottom + gap
    : Math.max(margin, at.top - gap - size.height);
  const centred = at.left + at.width / 2 - size.width / 2;
  const left = Math.max(
    margin,
    Math.min(centred, window.innerWidth - margin - size.width),
  );
  panel.style.top = `${top}px`;
  panel.style.left = `${left}px`;
}

/**
 * A citation of the Leistungsverzeichnis, drawn as a small ⓘ where the text used to say
 * "(LV §9)".
 *
 * The screens name the section a rule comes from wherever they state one — that is what
 * makes them checkable against the contract — but in the running text it read as a code
 * to everybody who was not checking. The citation is still there, one hover, tap or Tab
 * away, and it now says what the section is about.
 *
 * - **A button, not a hover region.** Hover does not exist on the tablets LV §194 names:
 *   a tap pins the panel open and a tap anywhere else closes it. Keyboard focus opens it
 *   too, and Escape hides it even with the pointer still on it (WCAG 1.4.13).
 * - **Named "LV §9"** — the words that stood in the text before, so a screen reader reads
 *   a heading as it did. The panel, with the section titles, is its description.
 * - **In the top layer** (`popover`). `.card` clips its overflow and drawers scroll, so a
 *   panel positioned inside them was cut off by the very box it explained.
 */
export function LvRef({ sections }: { sections: string }) {
  const panelId = useId();
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLSpanElement>(null);
  const closing = useRef<number | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);

  const show = (): void => {
    window.clearTimeout(closing.current);
    setOpen(true);
  };
  const hide = useCallback((): void => {
    window.clearTimeout(closing.current);
    setOpen(false);
    setPinned(false);
  }, []);
  // A moment's grace, so the pointer can cross from the ⓘ to the panel without the
  // panel vanishing on the way (WCAG 1.4.13, "hoverable").
  const hideSoon = (): void => {
    if (pinned) return;
    window.clearTimeout(closing.current);
    closing.current = window.setTimeout(hide, 150);
  };

  useEffect(() => () => window.clearTimeout(closing.current), []);

  useLayoutEffect(() => {
    const element = panel.current;
    const anchor = button.current;
    if (!open || !element || !anchor) return undefined;
    // jsdom has no popover API; the panel then renders in place, which is all a unit
    // test needs.
    if ("showPopover" in element) element.showPopover();
    placePanel(element, anchor);

    // In the capture phase, and stopped there: inside a dialog, Escape closes the panel
    // and leaves the dialog open. `useEscapeToClose` listens on the same document in the
    // bubble phase, so without this one key press dismissed both.
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      hide();
    };
    const onPointer = (event: PointerEvent): void => {
      if (!anchor.contains(event.target as Node)) hide();
    };
    // A fixed panel does not travel with the page, so it follows its ⓘ, and goes once the
    // ⓘ has left the viewport. Closing on every scroll shut it at once for a keyboard
    // user: Tab onto an ⓘ below the fold scrolls the page a frame after it opened, and
    // focus was still on it (WCAG 1.4.13 "persistent"; PR 131 review).
    const onMove = (): void => {
      const at = anchor.getBoundingClientRect();
      if (at.bottom < 0 || at.top > window.innerHeight) hide();
      else placePanel(element, anchor);
    };
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("pointerdown", onPointer);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("pointerdown", onPointer);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, hide]);

  return (
    // The panel is the button's own child, so pointing at it is still pointing at the
    // button — `jsx-a11y` rightly refuses hover handlers on a wrapper that is not a
    // control. It stays out of the button's name: `aria-label` takes precedence.
    <button
      ref={button}
      type="button"
      className="lv-ref"
      aria-label={`LV ${sections}`}
      aria-describedby={open ? panelId : undefined}
      onMouseEnter={show}
      onMouseLeave={hideSoon}
      onFocus={show}
      onBlur={() => {
        if (!pinned) hide();
      }}
      onClick={() => {
        if (pinned) {
          hide();
        } else {
          setPinned(true);
          show();
        }
      }}
    >
      <Info className="lv-ref__icon" aria-hidden="true" />
      {open ? (
        <span
          ref={panel}
          id={panelId}
          role="tooltip"
          popover="manual"
          className="lv-ref__panel"
        >
          <span className="lv-ref__heading">Leistungsverzeichnis</span>
          {citedSections(sections).map((section) => (
            <span key={section} className="lv-ref__line">
              <b>§{section}</b>
              {LV_SECTION_TITLES[section] ? ` · ${LV_SECTION_TITLES[section]}` : null}
            </span>
          ))}
        </span>
      ) : null}
    </button>
  );
}

/**
 * "(LV §9)", "(LV §90, §187)" and "(Vier-Augen-Prinzip, LV §59)", each with the full stop
 * that may follow it. The bracket's other words are kept; only the citation leaves.
 */
const CITATION =
  /\s*\((?:([^()]*?),\s*)?LV (§\d+(?:\s*[-–]\s*\d+)?(?:,\s*§\d+(?:\s*[-–]\s*\d+)?)*)(?:,\s*([^()]*?))?\)(\.?)/g;

/**
 * A text that may carry "(LV §n)", with each citation drawn as an `LvRef` after its
 * sentence.
 *
 * For the strings the client does not compose as markup: the server's refusal messages,
 * which `ErrorAlert` shows verbatim (LV §126), and toasts, whose message is a string.
 * Markup the client writes itself places `LvRef` directly.
 */
export function LvText({ text }: { text: string }) {
  const parts: ReactNode[] = [];
  let rest = 0;
  for (const match of text.matchAll(CITATION)) {
    const [whole, before, sections = "", after, stop] = match;
    parts.push(text.slice(rest, match.index));
    const kept = [before, after].filter(Boolean).join(", ");
    if (kept) parts.push(` (${kept})`);
    if (stop) parts.push(stop);
    parts.push(<LvRef key={match.index} sections={sections} />);
    rest = match.index + whole.length;
  }
  if (parts.length === 0) return <>{text}</>;
  parts.push(text.slice(rest));
  return <>{parts}</>;
}

// ==================================================================== alert
export function Alert({
  tone = "info",
  title,
  children,
  code,
}: {
  tone?: "info" | "warning" | "danger" | "ok";
  title?: ReactNode;
  children?: ReactNode;
  code?: string;
}) {
  return (
    <div
      className={`alert alert--${tone}`}
      role={tone === "danger" ? "alert" : "status"}
    >
      <div className="alert__body">
        {title ? <div className="alert__title">{title}</div> : null}
        {children ? <div>{children}</div> : null}
        {code ? <div className="alert__code">{code}</div> : null}
      </div>
    </div>
  );
}

/**
 * Render an API error.
 *
 * Shows the server's German message verbatim (LV §126) — the backend is the authority
 * on why an action was refused, and a frontend that re-worded refusals would drift from
 * the rules that produce them. The `request_id` is shown for a 5xx because that is what
 * support needs to find the entry in the structured logs (LV §118, §140).
 */
export function ErrorAlert({ error }: { error: unknown }) {
  if (!error) return null;

  if (error instanceof ApiError) {
    const tone = error.isBusinessRule || error.isConflict ? "warning" : "danger";
    return (
      <Alert
        tone={tone}
        title={error.isConflict ? "Änderungskonflikt" : "Aktion nicht möglich"}
        code={
          error.status >= 500 && error.requestId
            ? `${error.code} · Request-ID ${error.requestId}`
            : error.code
        }
      >
        <LvText text={error.message} />
      </Alert>
    );
  }

  return (
    <Alert tone="danger" title="Unerwarteter Fehler">
      {error instanceof Error ? error.message : formatValue(error)}
    </Alert>
  );
}

// ==================================================================== fields (LV §126)
export function Field({
  label,
  required,
  hint,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: ReactNode;
  error?: string;
  children: (id: string, invalid: boolean) => ReactNode;
}) {
  const id = useId();
  const invalid = Boolean(error);
  return (
    <div className="field">
      <label
        className={`field__label${required ? " field__required" : ""}`}
        htmlFor={id}
      >
        {label}
      </label>
      {children(id, invalid)}
      {hint && !error ? <span className="field__hint">{hint}</span> : null}
      {error ? (
        <span className="field__error" role="alert">
          {/* Often the server's refusal, routed onto its field with `setServerError`,
              and that text can carry "(LV §214)" like any other refusal. */}
          <LvText text={error} />
        </span>
      ) : null}
    </div>
  );
}

export function TextInput({
  invalid,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & {
  invalid?: boolean;
  /**
   * React 19 passes `ref` as an ordinary prop to a function component, so this needs
   * declaring but not `forwardRef`. The header search uses it to move focus into the
   * field the moment it expands.
   */
  ref?: React.Ref<HTMLInputElement>;
}) {
  return (
    <input
      {...props}
      className={`input${invalid ? " input--error" : ""}`}
      aria-invalid={invalid || undefined}
    />
  );
}

export function Select({
  invalid,
  children,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement> & { invalid?: boolean }) {
  return (
    <select
      {...props}
      className={`select${invalid ? " select--error" : ""}`}
      aria-invalid={invalid || undefined}
    >
      {children}
    </select>
  );
}

export function Textarea({
  invalid,
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }) {
  return (
    <textarea
      {...props}
      className={`textarea${invalid ? " textarea--error" : ""}`}
      aria-invalid={invalid || undefined}
    />
  );
}

export function Checkbox({
  label,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  const id = useId();
  return (
    <div className="checkbox-row">
      <input {...props} type="checkbox" id={id} />
      <label htmlFor={id}>{label}</label>
    </div>
  );
}

// ==================================================================== DetailDrawer
export function Drawer({
  title,
  subtitle,
  onClose,
  footer,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
}) {
  useEscapeToClose(onClose);
  const ref = useFocusTrap<HTMLDivElement>();
  const headingId = useId();
  const subtitleId = useId();

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} aria-hidden="true" />
      {/* A `div`, not an `aside`. `<aside>` has an implicit `complementary` role and
          overriding it with `dialog` is invalid ARIA (axe `aria-allowed-role`) — and
          wrong on its own terms: a modal drawer is not content beside the page, it is
          content instead of it. `.drawer` is `position: fixed`, so the element type
          carries no layout. */}
      <div
        className="drawer"
        role="dialog"
        aria-modal="true"
        // The heading *is* the name (LV §193, WCAG 4.1.2). Without this the drawer
        // announces itself as "dialog" and nothing else, so a screen-reader user is
        // told that something opened and not what.
        aria-labelledby={headingId}
        aria-describedby={subtitle ? subtitleId : undefined}
        ref={ref}
      >
        <header className="drawer__header">
          <div className="page__title">
            <h2 id={headingId}>{title}</h2>
            {subtitle ? (
              <div className="page__subtitle" id={subtitleId}>
                {subtitle}
              </div>
            ) : null}
          </div>
          <Button variant="ghost" onClick={onClose} aria-label="Schließen">
            ✕
          </Button>
        </header>
        <div className="drawer__body">{children}</div>
        {footer ? <div className="drawer__footer">{footer}</div> : null}
      </div>
    </>
  );
}

// ==================================================================== Modal (LV §125)
export function Modal({
  title,
  onClose,
  onSubmit,
  submitLabel = "Speichern",
  submitDisabled,
  busy,
  size,
  children,
}: {
  title: ReactNode;
  onClose: () => void;
  onSubmit?: () => void;
  submitLabel?: string;
  submitDisabled?: boolean;
  busy?: boolean;
  size?: "sm";
  children: ReactNode;
}) {
  useEscapeToClose(onClose);
  const ref = useFocusTrap<HTMLFormElement>();
  const headingId = useId();

  return (
    // The role sits on the backdrop rather than on the `<form>`: `role="dialog"` on a
    // form element is invalid ARIA (axe `aria-allowed-role`), and the backdrop is
    // already the layer that *is* the modal — no extra element, no CSS change. The form
    // stays a form, so the browser's own submit handling and validation keep working.
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
    >
      <form
        className={`modal${size ? ` modal--${size}` : ""}`}
        ref={ref}
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit?.();
        }}
      >
        <header className="modal__header">
          <h2 id={headingId}>{title}</h2>
        </header>
        <div className="modal__body">{children}</div>
        <footer className="modal__footer">
          {/* Explicit cancel and save — never `confirm()` (LV §125). */}
          <Button onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          {onSubmit ? (
            <Button type="submit" variant="primary" disabled={submitDisabled || busy}>
              {busy ? "Wird gespeichert…" : submitLabel}
            </Button>
          ) : null}
        </footer>
      </form>
    </div>
  );
}

/**
 * A confirmation dialog.
 *
 * Exists because LV §125 rules out `confirm()`. A real dialog can carry the German
 * consequence text, require a typed reason where LV §104 demands one, and be styled to
 * distinguish a destructive action from a routine one — none of which `confirm()` can do.
 *
 * The reason is a request-body field like any other, so it is a schema-validated form
 * (LV §6, #316). Its floor is the server's and differs by endpoint: a status action's
 * reason only has to be non-empty (`require_reason`, `app/audit/service.py`), while the
 * dedicated restore and deactivation bodies declare `min_length=3`. All but one hold at
 * most 2000 characters. The caller names its endpoint's limits; a single
 * three-character minimum for all of them was stricter than the server for every status
 * action (decision 126's rule: never stricter than the server).
 */
export function Confirm({
  title,
  message,
  confirmLabel = "Bestätigen",
  tone = "default",
  requireReason,
  reasonMinLength = 1,
  reasonMaxLength = 2000,
  reasonLabel = "Begründung",
  busy,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  tone?: "default" | "danger";
  /** Set where LV §104 requires a reason. The confirm button stays disabled until given. */
  requireReason?: boolean;
  /**
   * The endpoint's minimum, after trimming — `3` where its body says `min_length=3`.
   * Left out, a reason only has to be there, which is the status actions' rule.
   */
  reasonMinLength?: number;
  /** The endpoint's maximum. 2000 everywhere but where the body says `LongText`. */
  reasonMaxLength?: number;
  reasonLabel?: string;
  busy?: boolean;
  onConfirm: (reason?: string) => void;
  onCancel: () => void;
}) {
  const schema = useMemo(
    () =>
      z.object({
        reason: requireReason
          ? pflichttext(reasonMaxLength, reasonMinLength)
          : z.string(),
      }),
    [requireReason, reasonMinLength, reasonMaxLength],
  );
  const form = useSchemaForm(schema, { reason: "" });
  // The two behaviours `Modal` had and this did not. A confirmation is the dialog most
  // likely to be answered by keyboard — it is one of two buttons — and it was the one
  // that could not be dismissed with Escape and let Tab wander onto the page behind it.
  useEscapeToClose(onCancel);
  const ref = useFocusTrap<HTMLFormElement>();
  const headingId = useId();
  const messageId = useId();

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      // The consequence text, so "Wirklich löschen?" is announced with what will
      // happen rather than as a bare heading and two buttons (WCAG 4.1.2).
      aria-describedby={messageId}
    >
      <form
        className="modal modal--sm"
        ref={ref}
        onSubmit={(event) => {
          event.preventDefault();
          form.submit(({ reason }) => onConfirm(requireReason ? reason : undefined));
        }}
      >
        <header className="modal__header">
          <h2 id={headingId}>{title}</h2>
        </header>
        <div className="modal__body">
          <div id={messageId}>{message}</div>
          {requireReason ? (
            <Field
              label={reasonLabel}
              required
              error={form.errors.reason}
              hint={
                <>
                  Für diese Änderung ist eine Begründung erforderlich.
                  <LvRef sections="§104" />
                </>
              }
            >
              {(id, invalid) => (
                <Textarea
                  id={id}
                  invalid={invalid}
                  rows={3}
                  autoFocus
                  {...form.field("reason")}
                />
              )}
            </Field>
          ) : null}
        </div>
        <footer className="modal__footer">
          <Button onClick={onCancel} disabled={busy}>
            Abbrechen
          </Button>
          <Button
            type="submit"
            variant={tone === "danger" ? "danger" : "primary"}
            disabled={!form.isValid || busy}
          >
            {busy ? "Wird ausgeführt…" : confirmLabel}
          </Button>
        </footer>
      </form>
    </div>
  );
}

// ==================================================================== Permission Guard
/**
 * Show children only if the user holds the permission.
 *
 * A **UI convenience** (LV §134). The server checks every request; this only avoids
 * offering a button that would return 403. Hiding it is not a security control, and the
 * component name says `Guard` rather than `Authorize` for that reason.
 */
export function Guard({
  permission,
  fallback = null,
  children,
}: {
  permission: string | string[];
  fallback?: ReactNode;
  children: ReactNode;
}) {
  // Subscribed, not just read: `hasPermission` is a snapshot of a module variable, and
  // without this the button stays hidden when the shell rendered before `GET /me`
  // returned. See `useSession`.
  useSession();
  const permissions = Array.isArray(permission) ? permission : [permission];
  const allowed = permissions.some(hasPermission);
  return <>{allowed ? children : fallback}</>;
}

// ==================================================================== data view
export function Table({
  columns,
  children,
}: {
  columns: {
    key: string;
    label: string;
    align?: "right";
    /**
     * Give this column the table's surplus width so the others settle at their content
     * width (LV §124). At most one per table; without it the surplus is shared out and
     * short values drift apart. The matching `grow` class must also be set on the body
     * cell — `Table` only owns the header row.
     *
     * **Put it on the LAST column.** This was first shipped on Sites' *Name* column,
     * which measured worse than declaring nothing at all — 963px of record span at
     * 1251px against 841px for no `grow`, because surplus given to a column *inside*
     * the record splits every row into an identity block and an attribute block with a
     * canyon between them. On the trailing column the same surplus becomes a right
     * margin and the record stays together.
     */
    grow?: boolean;
  }[];
  children: ReactNode;
}) {
  return (
    <div className="table-scroll">
      <table className="data">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.key}
                className={
                  [
                    column.align === "right" ? "right" : null,
                    column.grow ? "grow" : null,
                  ]
                    .filter(Boolean)
                    .join(" ") || undefined
                }
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function EmptyState({
  title,
  children,
  action,
}: {
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <div className="empty__title">{title}</div>
      {children ? <div>{children}</div> : null}
      {action}
    </div>
  );
}

export function Loading({ rows = 5 }: { rows?: number }) {
  return (
    <div className="col" aria-busy="true" aria-live="polite">
      <span className="visually-hidden">Daten werden geladen…</span>
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="skeleton"
          style={{ width: `${100 - index * 6}%` }}
        />
      ))}
    </div>
  );
}

/**
 * Server-side pagination controls (LV §122).
 *
 * Shows the total from the server rather than counting rows: the whole point of
 * server-side paging is that the client never holds the full set.
 */
export function Pagination({
  page,
  pageSize,
  total,
  onPageChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}) {
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="pagination">
      <span>
        {from}–{to} von {total}
      </span>
      <div className="pagination__controls">
        <Button size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
          Zurück
        </Button>
        <span className="small">
          Seite {page} / {lastPage}
        </span>
        <Button
          size="sm"
          disabled={page >= lastPage}
          onClick={() => onPageChange(page + 1)}
        >
          Weiter
        </Button>
      </div>
    </div>
  );
}

// ==================================================================== detail list
export function Details({ children }: { children: ReactNode }) {
  return <dl className="details">{children}</dl>;
}

export function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="details__item">
      <dt className="details__label">{label}</dt>
      <dd className="details__value" style={{ margin: 0 }}>
        {children ?? <span className="muted">—</span>}
      </dd>
    </div>
  );
}

export function Stat({
  label,
  value,
  meta,
  tone,
  onClick,
}: {
  label: string;
  value: ReactNode;
  meta?: ReactNode;
  tone?: "ok" | "warning" | "danger";
  onClick?: () => void;
}) {
  const className = `stat${tone ? ` stat--${tone}` : ""}`;
  const content = (
    <>
      <span className="stat__label">{label}</span>
      <span className="stat__value">{value}</span>
      {meta ? <span className="stat__meta">{meta}</span> : null}
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        className={className}
        onClick={onClick}
        style={{ textAlign: "left" }}
      >
        {content}
      </button>
    );
  }
  return <div className={className}>{content}</div>;
}

// ==================================================================== hooks
function useEscapeToClose(onClose: () => void): void {
  useEffect(() => {
    const handler = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);
}

/**
 * Keep focus inside a dialog while it is open.
 *
 * Without this, tabbing out of a modal lands on the page behind it — which on a
 * touch-and-keyboard tablet means a user can edit the list underneath the form they
 * think they are filling in.
 */
function useFocusTrap<T extends HTMLElement>() {
  const ref = useRef<T>(null);

  useEffect(() => {
    const container = ref.current;
    if (!container) return;

    const previous = document.activeElement as HTMLElement | null;
    const focusable = [
      ...container.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ];
    // Not an LV ⓘ, though one is often first — in a dialog's message, above its first
    // field. It opens on focus, so its panel covered the dialog the moment it appeared;
    // and nobody opens a dialog in order to read a citation.
    (
      focusable.find((element) => !element.classList.contains("lv-ref")) ?? focusable[0]
    )?.focus();

    const handler = (event: KeyboardEvent): void => {
      if (event.key !== "Tab") return;
      const items = container.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener("keydown", handler);
    return () => {
      container.removeEventListener("keydown", handler);
      previous?.focus();
    };
  }, []);

  return ref;
}

/**
 * Dirty-state detection (LV §125).
 *
 * Warns before losing unsaved edits. The prototype had no such notion — closing a
 * `contenteditable` cell simply discarded it — and LV §125 asks for the opposite.
 */
export function useUnsavedChangesWarning(isDirty: boolean): void {
  useEffect(() => {
    if (!isDirty) return;
    const handler = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      // The browser shows its own text; the value is only a legacy requirement.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);
}

/** Debounce a filter value so typing does not fire a request per keystroke. */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

// ==================================================================== toasts
export interface Toast {
  id: string;
  tone: "info" | "ok" | "warning" | "danger";
  message: string;
}

export function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  // Pending dismissals, cleared on unmount like `useDebounced`'s timer above. Left
  // running, a page closed within the 4.5 s set state on an unmounted component — and
  // in the unit suite under load the timer fired after jsdom was torn down, failing the
  // run with "window is not defined" while every test passed (#413).
  const timers = useRef(new Set<number>());
  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((timer) => window.clearTimeout(timer));
      pending.clear();
    };
  }, []);

  const push = useCallback((tone: Toast["tone"], message: string): void => {
    const toast: Toast = { id: crypto.randomUUID(), tone, message };
    setToasts((current) => [...current, toast]);
    // A danger toast stays longer: it usually carries a German rule message the user
    // needs time to read.
    const timer = window.setTimeout(
      () => {
        timers.current.delete(timer);
        setToasts((current) => current.filter((item) => item.id !== toast.id));
      },
      tone === "danger" ? 9000 : 4500,
    );
    timers.current.add(timer);
  }, []);

  const region = (
    <div className="toast-region" aria-live="polite">
      {toasts.map((toast) => (
        <div key={toast.id} className={`toast toast--${toast.tone}`}>
          <LvText text={toast.message} />
        </div>
      ))}
    </div>
  );

  return { push, region };
}
